import type { CreateUserRequest, RoleDto, UserDto } from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  InfoNote,
  PageHeader,
  Spinner,
  inputClass,
} from '../components/ui';
import { DataTable, type DataColumn } from '../components/DataTable';
import { FilterBar } from '../components/FilterBar';
import { ApiError, api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useSession } from '../lib/session';

const EMPTY = {
  email: '',
  name: '',
  password: '',
  roleIds: [] as string[],
  mustChangePassword: true,
};

/**
 * Tick the roles a person holds. More than one is normal — a warehouse
 * supervisor who also releases quarantine — and the person keeps every one of
 * them at once: permissions are the union, so a second role only ever adds.
 */
function RolePicker({
  roles,
  selected,
  onToggle,
  disabled,
}: {
  roles: RoleDto[];
  selected: string[];
  onToggle: (roleId: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2">
      {roles.map((role) => (
        <label
          key={role.id}
          className="flex items-center gap-2 text-sm text-ink-body"
          title={role.description ?? undefined}
        >
          <input
            type="checkbox"
            checked={selected.includes(role.id)}
            disabled={disabled}
            onChange={() => onToggle(role.id)}
          />
          <span>
            {role.name}
            {role.isSystem ? null : <span className="ml-1 text-ink-subtle">(custom)</span>}
          </span>
        </label>
      ))}
      {roles.length === 0 ? (
        <span className="text-sm text-ink-muted">No roles are defined yet.</span>
      ) : null}
    </div>
  );
}

export function UsersPage() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useSession();
  const [includeInactive, setIncludeInactive] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState<{ userId: string; roleIds: string[] } | null>(null);
  const [resetting, setResetting] = useState<{
    userId: string;
    password: string;
    mustChangePassword: boolean;
  } | null>(null);

  const listQuery = useQuery({
    queryKey: ['users', { includeInactive }],
    queryFn: () => api.users.list({ includeInactive, limit: 100 }),
  });

  /**
   * Roles come from the API rather than a fixed list in the bundle, which is the
   * whole point of custom roles: one created a minute ago is assignable without a
   * redeploy, and one that has been deleted stops being offered.
   */
  const rolesQuery = useQuery({
    queryKey: ['roles', {}],
    queryFn: () => api.roles.list(),
  });
  const roles: RoleDto[] = rolesQuery.data?.items ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const createMutation = useMutation({
    mutationFn: (payload: CreateUserRequest) => api.users.create(payload),
    onSuccess: () => {
      void invalidate();
      setForm(EMPTY);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (variables: { id: string; body: Parameters<typeof api.users.update>[1] }) =>
      api.users.update(variables.id, variables.body),
    onSuccess: () => {
      void invalidate();
      setEditing(null);
      setResetting(null);
    },
  });

  function toggle(list: string[], roleId: string): string[] {
    return list.includes(roleId) ? list.filter((id) => id !== roleId) : [...list, roleId];
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    createMutation.mutate({
      email: form.email.trim().toLowerCase(),
      name: form.name.trim(),
      password: form.password,
      mustChangePassword: form.mustChangePassword,
      // Left out entirely rather than guessed at, so the API's own default (the
      // least privileged role) applies and there is only one place that decides
      // what "no choice" means.
      ...(form.roleIds.length > 0 ? { roleIds: form.roleIds } : {}),
    });
  }

  const users = listQuery.data?.items ?? [];
  const error = createMutation.error ?? updateMutation.error ?? listQuery.error;

  const userColumns: DataColumn<UserDto>[] = [
    {
      key: 'email',
      header: 'Email',
      cell: (user) => (
        <span className="font-mono text-xs">
          {user.email}
          {user.id === currentUser?.id ? (
            <span className="ml-2 text-ink-muted">(you)</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      primary: true,
      cell: (user) => <span className="font-medium text-ink">{user.name}</span>,
    },
    {
      key: 'roles',
      header: 'Roles',
      cell: (user) => (
        <span className="flex flex-wrap gap-1">
          {user.roles.map((role) => (
            <span
              key={role.id}
              className="rounded-sm bg-surface px-1.5 py-0.5 text-[11px] font-medium text-ink-body"
            >
              {role.name}
            </span>
          ))}
        </span>
      ),
    },
    {
      key: 'lastSignIn',
      header: 'Last sign-in',
      align: 'right',
      cell: (user) => (
        <span className="whitespace-nowrap text-xs">{formatDateTime(user.lastLoginAt)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (user) => (
        <span className="flex flex-wrap items-center gap-1">
          {user.isActive ? (
            <Badge tone="green">Active</Badge>
          ) : (
            <Badge tone="muted">Deactivated</Badge>
          )}
          {user.mustChangePassword ? <Badge tone="hazard">Temp password</Badge> : null}
        </span>
      ),
    },
  ];

  const userActions = (user: UserDto) => {
    const isSelf = user.id === currentUser?.id;
    return (
      <>
        <Button
          tone="ghost"
          disabled={isSelf}
          title={isSelf ? 'You cannot change your own roles. Ask another administrator.' : undefined}
          onClick={() => {
            updateMutation.reset();
            setResetting(null);
            setEditing({ userId: user.id, roleIds: user.roles.map((role) => role.id) });
          }}
        >
          Edit roles
        </Button>
        <Button
          tone="ghost"
          disabled={isSelf}
          title={isSelf ? 'Use your own profile to change your password.' : undefined}
          onClick={() => {
            updateMutation.reset();
            setEditing(null);
            setResetting({ userId: user.id, password: '', mustChangePassword: true });
          }}
        >
          Reset password
        </Button>
        <Button
          tone={user.isActive ? 'danger' : 'ghost'}
          disabled={isSelf}
          title={isSelf ? 'You cannot deactivate your own account' : undefined}
          onClick={() => updateMutation.mutate({ id: user.id, body: { isActive: !user.isActive } })}
        >
          {user.isActive ? 'Deactivate' : 'Reactivate'}
        </Button>
      </>
    );
  };

  const userDetail = (user: UserDto) => {
    if (resetting?.userId === user.id) {
      return (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            updateMutation.mutate({
              id: user.id,
              // A reset revokes every session server-side, so the old password
              // stops working anywhere the moment this saves.
              body: {
                password: resetting.password,
                mustChangePassword: resetting.mustChangePassword,
              },
            });
          }}
        >
          <p className="text-[10px] font-bold uppercase tracking-widest text-ink-muted">
            Reset password for {user.email}
          </p>
          <div className="mt-2 max-w-sm">
            <Field label="New password" hint="At least 12 characters">
              <input
                className={inputClass}
                type="password"
                autoComplete="new-password"
                value={resetting.password}
                onChange={(event) =>
                  setResetting({ ...resetting, password: event.target.value })
                }
                required
                minLength={12}
              />
            </Field>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-ink-body">
            <input
              type="checkbox"
              checked={resetting.mustChangePassword}
              onChange={(event) =>
                setResetting({ ...resetting, mustChangePassword: event.target.checked })
              }
            />
            Require them to choose a new password at next sign-in
          </label>
          <p className="mt-2 text-xs text-ink-muted">
            This signs them out of every device immediately.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              type="submit"
              disabled={updateMutation.isPending || resetting.password.length < 12}
            >
              {updateMutation.isPending ? 'Saving…' : 'Reset password'}
            </Button>
            <Button tone="ghost" onClick={() => setResetting(null)}>
              Cancel
            </Button>
            {updateMutation.error instanceof ApiError ? (
              <span className="text-xs text-red-700">{updateMutation.error.message}</span>
            ) : null}
          </div>
        </form>
      );
    }

    if (editing?.userId === user.id) {
      return (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            updateMutation.mutate({ id: user.id, body: { roleIds: editing.roleIds } });
          }}
        >
          <p className="text-[10px] font-bold uppercase tracking-widest text-ink-muted">
            Roles for {user.email}
          </p>
          <div className="mt-2">
            <RolePicker
              roles={roles}
              selected={editing.roleIds}
              disabled={updateMutation.isPending}
              onToggle={(roleId) =>
                setEditing({ ...editing, roleIds: toggle(editing.roleIds, roleId) })
              }
            />
          </div>
          <p className="mt-2 text-xs text-ink-muted">
            Ticking replaces the whole set. At least one role is required — an account with none
            could sign in and do nothing.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              type="submit"
              disabled={updateMutation.isPending || editing.roleIds.length === 0}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save roles'}
            </Button>
            <Button tone="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            {updateMutation.error instanceof ApiError ? (
              <span className="text-xs text-red-700">{updateMutation.error.message}</span>
            ) : null}
          </div>
        </form>
      );
    }

    return null;
  };

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Users and roles"
        description="A role is a bundle of capabilities. The API checks capabilities on every request, so a role you cannot see here cannot be worked around by hiding a button."
      />

      <div className="mb-6">
        <InfoNote>
          A person can hold <strong>several roles</strong>; their permissions are all of them
          combined, so a second role only ever adds. When somebody holds more than one they get a
          view picker in the header, which changes which screens are shown — never what they are
          allowed to do.
        </InfoNote>
      </div>

      <Card className="mb-6">
        <CardBody>
          <h2 className="text-base font-bold text-ink">New user</h2>
          <form autoComplete="off" onSubmit={handleSubmit} className="mt-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Email">
                <input
                  className={inputClass}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  placeholder="person@gsls.local"
                />
              </Field>
              <Field label="Name">
                <input
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="Full name"
                />
              </Field>
              <Field label="Password" hint="At least 12 characters">
                <input
                  name="password"
                  className={inputClass}
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={12}
                />
              </Field>
            </div>

            <label className="mt-4 flex items-center gap-2 text-sm text-ink-body">
              <input
                type="checkbox"
                checked={form.mustChangePassword}
                onChange={(e) => setForm({ ...form, mustChangePassword: e.target.checked })}
              />
              Require them to choose a new password at first sign-in
            </label>

            <div className="mt-4">
              <Field
                label="Roles"
                tooltip="Tick every role this person needs. They hold all of them at once, and their permissions are the union — there is no way for one role to take away what another grants."
              >
                <RolePicker
                  roles={roles}
                  selected={form.roleIds}
                  onToggle={(roleId) => setForm({ ...form, roleIds: toggle(form.roleIds, roleId) })}
                />
              </Field>
              <p className="mt-2 text-xs text-ink-muted">
                {form.roleIds.length === 0
                  ? 'Nothing ticked, so this account gets the Viewer role — it can read everything and change nothing.'
                  : `${form.roleIds.length} role${form.roleIds.length === 1 ? '' : 's'} selected.`}
              </p>
            </div>

            <ErrorNote>
              {createMutation.error instanceof ApiError ? createMutation.error.message : ''}
            </ErrorNote>
            <div className="mt-3">
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create user'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <FilterBar>
            <div className="mb-4 flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-ink-body">
                <input
                  name="includeInactive"
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                Include deactivated
              </label>
              {listQuery.isSuccess ? (
                <span className="text-sm text-ink-muted">{listQuery.data.total} user(s)</span>
              ) : null}
            </div>
          </FilterBar>

          {listQuery.isPending ? <Spinner /> : null}
          <ErrorNote>
            {error instanceof ApiError ? error.message : listQuery.isError ? 'Could not load users.' : ''}
          </ErrorNote>

          {listQuery.isSuccess ? (
            <DataTable
              rows={users}
              columns={userColumns}
              rowKey={(user) => user.id}
              rowClassName={() => 'hover:bg-brand-soft/40'}
              actionsHeader="Actions"
              actions={userActions}
              detail={userDetail}
              empty="No users."
            />
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}
